import type { ChangeDefinition, CommonImportParameters } from '@sap/ux-specification-types';
import {
    v2,
    ControlType,
    DefinitionName,
    PAGETYPE_VIEW_EXTENSION_TEMPLATE_MAP,
    PageTypeV2,
    VOCWITHCOLONS
} from '@sap/ux-specification-types';
import type { Definition } from 'typescript-json-schema';
import { parseChangeString } from '../../../common';

/**
 * Flex change structure for the transfer to the flexiblity utils
 */
export type FlexForTransfer = {
    controlId: string;
    controlType: string;
    propertyId: string;
    facetId?: string;
    newValue: any;
    creation: string;
};

export interface FlexChangesForTransfer {
    [controlId: string]: FlexForTransfer;
}

export type ConfigTableType = v2.ResponsiveTable | v2.TreeTable | v2.AnalyticalTable | v2.GridTable | v2.TableALP;

/**
 * Checks whether a given flex change applies to a given table column. If this is the case the change is filled into the config.
 *
 * @param {ConfigTableType} config - the table configuration
 * @param {FlexForTransfer} change - the flex change
 * @param {string} columnKey - the column key. If the column key is not equal to the column id of the change, the change will not be added to the column.
 * @param {object} customColumn - the custom column. If set, the property of the change will be added to the given column.
 */
export function addFlexChangeToConfigTableColumn(
    config: ConfigTableType,
    change: FlexForTransfer,
    columnKey: string,
    customColumn?: object
): void {
    if (change.controlType === ControlType.TableColumn || change.controlType === ControlType.Button) {
        const idParts = change.controlId.split('-');
        const lastIdPart = idParts[idParts.length - 1];
        // As the last stable Id part is still too long in some cases, strip off the fixed parts.
        // It must the be added again by the export!
        const annotationPart =
            change.controlType === ControlType.TableColumn
                ? lastIdPart.split('template:::TableColumn:::')
                : [lastIdPart];
        const colId = customColumn
            ? annotationPart[annotationPart.length - 1]
            : annotationPart[annotationPart.length - 1]
                  .replace(/sSmartTableId.+?:::/, '')
                  .replace('::com.sap.vocabularies', VOCWITHCOLONS);
        let columnKeyEncoded = columnKey?.replace(/@/g, ':40').replace(/#/g, ':23');
        if (columnKeyEncoded?.startsWith('DataField')) {
            //Regular encoding of stable ID's
            columnKeyEncoded = columnKeyEncoded?.replace('/', ':2f');
        } else {
            //legacy encoding, for smart table columns only
            columnKeyEncoded = columnKeyEncoded?.replace('/', '_');
        }
        //Compare column part of the selectorId with the given columnKey
        if (columnKeyEncoded !== colId && columnKey !== colId) {
            return;
        }
        //Add property of flex change to the column in config
        if (customColumn) {
            customColumn[change.propertyId] = change.newValue;
        } else {
            if (!config.columns) {
                config.columns = {};
            }
            if (!config.columns[columnKey]) {
                config.columns[columnKey] = {};
            }
            config.columns[columnKey][change.propertyId] = change.newValue;
        }
    }
}

/**
 * Fills a table column flex change into the config, if it is relevant for the table.
 *
 * @param {ConfigTableType} config - the table configuration
 * @param {FlexForTransfer} change - the flex change
 * @param lineItemsDefinition - the definition of line items in the schema
 */
export function addFlexChangeToConfigTable(
    config: ConfigTableType,
    change: FlexForTransfer,
    lineItemsDefinition: Definition
): void {
    if (
        change.controlType?.endsWith(DefinitionName.Table) &&
        Object.keys(lineItemsDefinition.properties).indexOf(change.propertyId) > -1
    ) {
        config[change.propertyId] = change.newValue;
    }
}

/**
 * Imports the flex changes for a table column.
 *
 * @param {CommonImportParameters} importParameters - common import parameters
 * @param {v2.ListReportConfigV} listReportConfig - the list report configuration
 * @param {string} columnKey - the column key
 * @param {object} customColumn - optional: the custom column
 * @param {string} lineItemsId - the line items id
 */
export function importFlexChangesOfColumn(
    importParameters: CommonImportParameters,
    listReportConfig: v2.ListReportConfigV2,
    columnKey: string,
    customColumn?: object,
    lineItemsId = 'LineItems'
) {
    //Check all flex changes against the given column key
    importParameters.flex.forEach((changeString) => {
        const change = parseChangeString(changeString) as ChangeDefinition;

        const flexForTransfer: FlexForTransfer = {
            controlId: change.controlId,
            propertyId: change.content?.property,
            newValue: change.content?.newBinding ? change.content.newBinding : change.content?.newValue,
            controlType: change.controlType,
            creation: change.creation
        };

        let schemaProperty;
        //The relevant schema property can either reside on column or table level
        if (change.controlType === ControlType.TableColumn) {
            schemaProperty =
                importParameters.jsonSchema['definitions'][`TableColumn`].properties[flexForTransfer.propertyId];
        } else {
            //Determine the right table type ID for the schema entry
            const tableTypeId =
                (!listReportConfig.table.type || listReportConfig.table.type === v2.TableTypeV2.ResponsiveTable) &&
                !flexForTransfer.controlId?.includes(
                    PAGETYPE_VIEW_EXTENSION_TEMPLATE_MAP.get(PageTypeV2.AnalyticalListPage)
                )
                    ? 'ResponsiveTableWithMultiSelect'
                    : listReportConfig.table.constructor.name;
            schemaProperty =
                importParameters.jsonSchema['definitions'][`${tableTypeId}<${lineItemsId}>`]?.properties[
                    flexForTransfer.propertyId
                ];
        }

        if (schemaProperty) {
            //Add the flex change to the config
            addFlexChangeToConfigTableColumn(listReportConfig.table, flexForTransfer, columnKey, customColumn);
        }
    });
}
